import { useEffect } from "react";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import { CustomerProvider } from "@/context/CustomerContext";
import ChatWidget from "@/components/ChatWidget";
import Track from "@/pages/Track";
import InstallPrompt from "@/components/InstallPrompt";
import BottomBar from "@/components/BottomBar";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] text-zinc-300 px-6">
          <div className="text-center max-w-md">
            <p className="font-serif text-3xl text-gold mb-3">Something caught the light</p>
            <p className="text-sm text-zinc-500 mb-6">An unexpected error occurred. Please refresh the page.</p>
            <button onClick={() => window.location.reload()} className="btn-gold" data-testid="error-refresh-button">
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/login" element={<Login />} />
        <Route path="/track" element={<Track />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Home />} />
      </Routes>
      {!isAdmin && <Footer />}
      {!isAdmin && <BottomBar />}
      <div className="h-[70px] md:hidden" />
      {!isAdmin && <InstallPrompt />}
      <ChatWidget />
      <CartDrawer />
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <CartProvider>
        <AuthProvider>
          <CustomerProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Shell />
            </BrowserRouter>
            <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#18181C", border: "1px solid rgba(212,175,55,0.25)", color: "#F4F4F6" } }} />
          </CustomerProvider>
        </AuthProvider>
      </CartProvider>
    </ErrorBoundary>
  );
}

export default App;
